import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Initialiser le bucket de stockage au démarrage
(async () => {
  try {
    await fetch('https://fyolnpydipryzeweubbp.supabase.co/functions/v1/create-storage-bucket');
  } catch {
    // Silencieux si déjà créé
  }
})();

export interface Product {
  id: string;
  name: string;
  price: number;
  image_url?: string;
  image?: string; // alias local pour compatibilité avec les composants
  images?: string[]; // Tableau de 3 photos supplémentaires maximum
  description?: string;
  category?: string;
  sizes?: string[];
  colors?: string[];
  stock?: number | { [key: string]: number };
  badge?: string;
  is_new?: boolean;
  is_promo?: boolean;
  promo_price?: number;
}

export interface Order {
  id?: string;
  orderNumber: string;
  customerName: string;
  phone: string;
  address: string;
  city: string;
  items: Array<{
    productId: string;
    productName: string;
    size: string;
    color: string;
    quantity: number;
    price: number;
  }>;
  total: number;
  status: 'En attente' | 'Confirmée' | 'En livraison' | 'Livrée' | 'Annulée';
  notes?: string;
  created_at?: string;
}

// Upload d'image produit
export async function uploadProductImage(file: File): Promise<string> {
  const fileExt = file.name.split('.').pop();
  const fileName = `${Math.random().toString(36).substring(2)}-${Date.now()}.${fileExt}`;
  const filePath = `products/${fileName}`;

  const { error: uploadError } = await supabase.storage
    .from('product-images')
    .upload(filePath, file);

  if (uploadError) {
    console.error('Error uploading image:', uploadError);
    throw new Error('Erreur lors de l\'upload de l\'image');
  }

  const { data } = supabase.storage
    .from('product-images')
    .getPublicUrl(filePath);

  return data.publicUrl;
}

// Upload de plusieurs images produit en parallèle
export async function uploadProductImages(files: File[]): Promise<string[]> {
  const uploadPromises = files.map(file => uploadProductImage(file));
  return Promise.all(uploadPromises);
}

// Utilitaire retry pour les erreurs temporaires Supabase (ex: PGRST002)
async function withRetry<T>(fn: () => Promise<T>, retries = 3, delay = 1500): Promise<T> {
  for (let i = 0; i < retries; i++) {
    try {
      const result = await fn();
      return result;
    } catch (err: unknown) {
      const isRetryable =
        err instanceof Error && err.message.includes('PGRST002');
      if (isRetryable && i < retries - 1) {
        await new Promise(res => setTimeout(res, delay));
      } else {
        throw err;
      }
    }
  }
  throw new Error('Max retries reached');
}

// Mapper les données Supabase vers le format utilisé dans l'app
function mapProduct(row: Record<string, unknown>): Product {
  return {
    ...row,
    id: row.id as string,
    name: row.name as string,
    price: row.price as number,
    image: (row.image_url as string) || '',
    image_url: row.image_url as string,
    images: (row.images as string[]) || [],
    description: row.description as string,
    category: row.category as string,
    sizes: (row.sizes as string[]) || [],
    colors: (row.colors as string[]) || [],
    stock: (row.stock as number) || 0,
    badge: row.badge as string,
    is_new: row.is_new as boolean,
    is_promo: row.is_promo as boolean,
    promo_price: row.promo_price as number,
  } as Product;
}

// Préparer les données produit pour l'envoi à Supabase
function toSupabaseProduct(product: Omit<Product, 'id'>): Record<string, unknown> {
  const { image, stock, ...rest } = product as Product & { image?: string };
  return {
    ...rest,
    image_url: image || product.image_url || '',
    images: product.images || [],
    // Si stock est un objet (par taille/couleur), calculer le total
    stock: typeof stock === 'object' && stock !== null
      ? Object.values(stock as { [key: string]: number }).reduce((a, b) => a + b, 0)
      : (stock as number) || 0,
  };
}

// Produits
export async function getProducts(): Promise<Product[]> {
  return withRetry(async () => {
    const { data, error } = await supabase
      .from('products')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching products:', JSON.stringify(error));
      if (error.code === 'PGRST002') throw new Error('PGRST002');
      return [];
    }

    return (data || []).map(mapProduct);
  });
}

export async function getProductById(id: string): Promise<Product | null> {
  try {
    const { data, error } = await supabase
      .from('products')
      .select('*')
      .eq('id', id)
      .maybeSingle();

    if (error) {
      console.error('Error fetching product:', error);
      return null;
    }

    return data ? mapProduct(data) : null;
  } catch {
    return null;
  }
}

export async function addProduct(product: Omit<Product, 'id'>): Promise<Product | null> {
  const payload = toSupabaseProduct(product);
  const { data, error } = await supabase
    .from('products')
    .insert([payload])
    .select()
    .maybeSingle();

  if (error) {
    console.error('Error creating product:', error);
    throw new Error('Erreur lors de la création du produit');
  }

  return data ? mapProduct(data) : null;
}

export async function updateProduct(id: string, updates: Partial<Product>): Promise<Product | null> {
  const payload = toSupabaseProduct(updates as Omit<Product, 'id'>);
  const { data, error } = await supabase
    .from('products')
    .update(payload)
    .eq('id', id)
    .select()
    .maybeSingle();

  if (error) {
    console.error('Error updating product:', error);
    throw new Error('Erreur lors de la mise à jour du produit');
  }

  return data ? mapProduct(data) : null;
}

export async function deleteProduct(id: string): Promise<boolean> {
  const { error } = await supabase
    .from('products')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error deleting product:', error);
    throw new Error('Erreur lors de la suppression du produit');
  }

  return true;
}

// Mapper les données Supabase vers le format Order utilisé dans l'app
function mapOrder(row: Record<string, unknown>): Order {
  return {
    id: row.id as string,
    orderNumber: (row.order_number as string) || '',
    customerName: (row.customer_name as string) || '',
    phone: (row.customer_phone as string) || '',
    address: (row.customer_address as string) || '',
    city: (row.customer_city as string) || '',
    items: (row.items as Order['items']) || [],
    total: row.total as number,
    status: (row.status as Order['status']) || 'En attente',
    notes: row.notes as string,
    created_at: row.created_at as string,
  };
}

// Commandes
export async function getOrders(): Promise<Order[]> {
  return withRetry(async () => {
    const { data, error } = await supabase
      .from('orders')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching orders:', JSON.stringify(error));
      if (error.code === 'PGRST002') throw new Error('PGRST002');
      return [];
    }

    return (data || []).map(mapOrder);
  });
}

export async function getOrderById(id: string): Promise<Order | null> {
  try {
    const { data, error } = await supabase
      .from('orders')
      .select('*')
      .eq('id', id)
      .maybeSingle();

    if (error) {
      console.error('Error fetching order:', error);
      return null;
    }

    return data ? mapOrder(data) : null;
  } catch {
    return null;
  }
}

export async function saveOrder(order: Omit<Order, 'id' | 'created_at'>): Promise<Order | null> {
  try {
    const payload = {
      order_number: order.orderNumber,
      customer_name: order.customerName,
      customer_phone: order.phone,
      customer_address: order.address,
      customer_city: order.city,
      items: order.items,
      total: order.total,
      status: order.status,
      notes: order.notes || null,
    };

    const { data, error } = await supabase
      .from('orders')
      .insert([payload])
      .select()
      .maybeSingle();

    if (error) {
      console.error('Error creating order:', JSON.stringify(error));
      throw new Error('Erreur lors de la création de la commande: ' + error.message);
    }

    return data ? mapOrder(data) : null;
  } catch (err) {
    console.error('saveOrder exception:', err);
    throw err;
  }
}

export async function createOrder(order: Omit<Order, 'id'>): Promise<Order | null> {
  return saveOrder(order);
}

export async function updateOrderStatus(id: string, status: Order['status']): Promise<Order | null> {
  const { data, error } = await supabase
    .from('orders')
    .update({ status })
    .eq('id', id)
    .select()
    .maybeSingle();

  if (error) {
    console.error('Error updating order status:', error);
    return null;
  }

  return data ? mapOrder(data) : null;
}

export async function updateProductStock(productId: string, size: string, color: string, quantity: number): Promise<boolean> {
  // Cette fonction met à jour le stock d'un produit
  // Pour l'instant, on retourne true car la gestion du stock sera implémentée selon votre structure de données
  console.log('Updating stock for product:', productId, size, color, quantity);
  return true;
}
